/**
 * Complément anglais propre à Beaconsfield.
 *
 * `utils/translate.ts` fusionne ce dictionnaire avec `phrases` de `data/translations.ts`.
 * Garder les ajouts ici évite de réécrire le gros fichier commun à chaque ville.
 *
 * Rappel sur le traducteur : il cherche d'abord une correspondance EXACTE (insensible à
 * la casse et aux espaces), puis des motifs de date, puis un repli mot à mot qui ne
 * s'applique que si tous les mots sont connus. Les noms propres — Beaconsfield,
 * Lakeshore, Windermere, Centennial — ne figurant pas au glossaire, les chaînes qui en
 * contiennent restent intactes. C'est voulu.
 *
 * Corollaire : une phrase coupée par une interpolation JSX devient deux nœuds de texte
 * et ne correspond plus à rien. Garder ici les phrases entières, telles qu'elles sont
 * écrites dans le composant.
 */
export const beaconsfieldPhrases: Record<string, string> = {
  // — En-tête et navigation
  'Ma ville': 'My city',
  'Culture et loisirs': 'Culture and recreation',
  'Nous joindre': 'Contact us',
  'Taille du texte': 'Text size',
  'Mon bciti+': 'My bciti+',
  'Navigation principale': 'Main navigation',
  'Rechercher sur le site': 'Search the site',
  'Vivre à Beaconsfield': 'Living in Beaconsfield',
  'Administration et finances': 'Administration and finance',
  'Votre conseil': 'Your council',
  'Services aux citoyens': 'Citizen services',
  'Environnement et travaux publics': 'Environment and public works',
  'Urbanisme et permis': 'Urban planning and permits',
  'Loisirs et vie communautaire': 'Recreation and community life',
  'Installations et parcs': 'Facilities and parks',
  'Bibliothèque et culture': 'Library and culture',

  // — Hero
  'En savoir plus': 'Learn more',
  'Découvrir': 'Discover',
  'En vedette': 'Featured',
  'À la une': 'Highlights',
  'Faire défiler vers le contenu': 'Scroll to content',
  'Demander à ATNA': 'Ask ATNA',
  'Demander à ATNA…': 'Ask ATNA…',
  'ATNA connaît les services de Beaconsfield': 'ATNA knows Beaconsfield’s services',
  'Nouvelle plateforme d’inscription': 'New registration platform',
  'Refonte du plan et des règlements de l’urbanisme': 'Urban planning and by-law overhaul',
  'Campagne Centre culturel Beaconsfield': 'Beaconsfield Cultural Centre campaign',
  'Rénovations du Centre récréatif': 'Recreation Centre renovations',
  'Nouveau portail d’alertes': 'New alerts portal',
  'Projet Connectons Beaconsfield': 'Connecting Beaconsfield project',
  'Bienvenue sur le site de la ville de Beaconsfield': 'Welcome to the City of Beaconsfield website',

  // Conservé : leur ancienne question du hero, si la carte verte revenait un jour.
  'Que recherchez-vous aujourd’hui ?': 'What are you looking for today?',

  // — Rail « Vos accès rapides »
  'Vos accès rapides': 'Your quick links',
  'Avis publics': 'Public notices',
  'Calendrier des collectes': 'Collection calendar',
  'Carte interactive': 'Interactive map',
  Emplois: 'Jobs',
  'Inscription en ligne': 'Online registration',
  'Permis et certificats': 'Permits and certificates',
  'Requêtes et signalements': 'Requests and reports',
  'Médailles animaux': 'Pet tags',

  // — « Quoi de neuf? »
  'Quoi de neuf?': 'What’s new?',
  'Toutes les actualités': 'All news',
  'Lire la suite': 'Read more',
  Environnement: 'Environment',
  'Sondage sur les communications de la Ville': 'Survey on the City’s communications',
  'Ouverture du dépôt à feuilles': 'Leaf drop-off site now open',

  // — Événements
  Prochainement: 'Coming up',
  'Calendrier des événements': 'Events calendar',
  'Tous les événements': 'All events',
  'Activités pour enfants': 'Children’s activities',
  'Ateliers et conférences': 'Workshops and talks',
  'Trousse à emporter : l’Action de grâce': 'Take-home kit: Thanksgiving',
  'L’heure de conversation : français': 'Conversation hour: French',
  'Disponibles au comptoir du prêt à partir du 6 octobre à 10 h':
    'Available at the loans desk from October 6 at 10 a.m.',
  'Places limitées, inscription au comptoir': 'Limited places, register at the desk',

  // — Cartes à découvrir et bandeau
  'À découvrir': 'Discover',
  Nouveau: 'New',
  'Outil de recherche': 'Search tool',
  Ressources: 'Resources',
  'Centre culturel': 'Cultural centre',
  'Recherche par adresse': 'Search by address',
  'Services aux aînés': 'Seniors’ services',
  'Tout au même endroit, bien plus simple': 'Everything in one place, much simpler',
  'Votre portail citoyen': 'Your citizen portal',
  'Portail citoyen': 'Citizen portal',

  // — Pied de page
  Abonnements: 'Subscriptions',
  'Pour ne rien manquer, inscrivez-vous à nos différents services':
    'Don’t miss a thing — sign up for our services',
  'S’inscrire': 'Sign up',
  'Directive linguistique': 'Language directive',
  Nétiquette: 'Netiquette',
  'Vous avez des questions ou voulez communiquer avec nous ?':
    'Do you have questions or want to get in touch?',
  '© Ville de Beaconsfield, 2026': '© City of Beaconsfield, 2026',
  'Retour en haut de la page': 'Back to top',
  'Urgences après heures :': 'After-hours emergencies:',

  // — Secteurs et entités de Beaconsfield
  'Sud de l’autoroute 20': 'South of Highway 20',
  'Nord de l’autoroute 20': 'North of Highway 20',
  'Secteur sud de l’autoroute 20': 'South of Highway 20 sector',
  'Secteur du chemin Beaurepaire': 'Beaurepaire Road sector',
  'Ville de Beaconsfield': 'City of Beaconsfield',
  'Hôtel de ville de Beaconsfield': 'Beaconsfield City Hall',
  'Bibliothèque de Beaconsfield': 'Beaconsfield Library',
  'Centre récréatif': 'Recreation Centre',
  'Centre récréatif de Beaconsfield': 'Beaconsfield Recreation Centre',
  'Centre culturel Beaconsfield': 'Beaconsfield Cultural Centre',
  'Travaux publics et site de dépôt': 'Public Works and drop-off site',
  'Service animalier': 'Animal services',
  'Parc Centennial': 'Centennial Park',
  'Rue Elm': 'Elm Street',
  '366, chemin Lakeshore': '366 Lakeshore Road',
  '130, Taywood Drive': '130 Taywood Drive',
  'Position actuelle': 'Current location',

  // — Collectes
  'Bac gris': 'Grey bin',
  'Bac bleu': 'Blue bin',
  'Sacs de papier ou bac': 'Paper bags or bin',
  'Branches et résidus verts': 'Branches and yard waste',
  'En bordure de rue': 'At the curb',
  'Ordures — bac gris': 'Garbage — grey bin',
  'Recyclage — bac bleu': 'Recycling — blue bin',
  'Encombrants et CRD': 'Bulky items and C&D waste',
  'Collecte des encombrants reportée': 'Bulky item collection postponed',
  'Collectes spéciales': 'Special collections',

  // — Espace citoyen et bibliothèque
  '2 documents à rapporter': '2 items to return',
  'Documents à rapporter': 'Items to return',
  'Voir ma carte': 'View my card',
  Abonnée: 'Member',
  'La nouvelle plateforme d’inscription est en ligne': 'The new registration platform is online',
  'Voir la programmation': 'View the program',
  'Séance du conseil municipal': 'City council meeting',
  'Salles, chalets de parc et plateaux': 'Halls, park chalets and sports facilities',
  'Chalet du parc Windermere': 'Windermere Park chalet',
  'Gymnase municipal': 'Municipal gymnasium',
  'Piscine communautaire Briarwood': 'Briarwood community pool',
  'Retrouvez votre carte, vos alertes et vos services dans l’application bciti+ de la Ville de Beaconsfield.':
    'Find your card, your alerts and your services in the City of Beaconsfield’s bciti+ app.',

  // — Animaux
  'Cet animal est enregistré à Beaconsfield. Son gardien vient d’être avisé qu’il a été trouvé. Vos coordonnées ne lui sont pas transmises : la Ville fait le lien.':
    'This pet is registered in Beaconsfield. Its guardian has just been notified that it was found. Your contact details are not shared with them — the City makes the connection.',
  'Médaille BEA-•••21 · avis envoyé à l’instant': 'Tag BEA-•••21 · notice sent just now',
}
